import { useRequest } from 'features/request/useRequest';
import type { BimPriceLevelEnum } from 'gql/graphql';
import { WorkOrderPriceRevisionTypeEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useEffect, useMemo } from 'react';

import { BimWorkOrderRowDetailsType } from '../../types';
import { bimWorkOrderPriceRevisionsGql } from '../bimWorkOrderPriceQueries';

import { getWhereVariables } from './helpers';

const requestId = 'bimWorkOrderPriceRevisions';

export const useBimWorkOrderPriceRevisions = (
    workOrderPriceId: number,
    detailsType: BimWorkOrderRowDetailsType,
    filterValues: Record<string, string[]>,
    priceLevel: BimPriceLevelEnum | undefined,
) => {
    const revisionType = useMemo<WorkOrderPriceRevisionTypeEnum>(
        () =>
            detailsType === BimWorkOrderRowDetailsType.Revision
                ? WorkOrderPriceRevisionTypeEnum.Revision
                : WorkOrderPriceRevisionTypeEnum.Update,
        [detailsType],
    );

    const variables = useMemo(
        () => ({
            workOrderPriceId,
            revisionType,
            priceLevel,
            where: getWhereVariables(filterValues),
        }),
        [filterValues, priceLevel, revisionType, workOrderPriceId],
    );

    const [bimWorkOrderPriceRevisionsData, , , bimWorkOrderPriceRevisionsRequest] = useRequest({
        requestId,
        query: bimWorkOrderPriceRevisionsGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: bimWorkOrderPriceRevisionsRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.items,
                bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.totalCount,
            );
        }
    }, [
        bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.items,
        bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.totalCount,
        getRowParams,
    ]);

    const bimWorkOrderPriceRevisionsItemsCount = useMemo(
        () => bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.totalCount ?? 0,
        [bimWorkOrderPriceRevisionsData?.bimWorkOrderPriceRevisions?.totalCount],
    );

    const bimWorkOrderPriceRevisionsFetchedItemsCounter = useMemo(() => {
        if (!getRowParams?.current?.endRow || !bimWorkOrderPriceRevisionsItemsCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > bimWorkOrderPriceRevisionsItemsCount) {
            return bimWorkOrderPriceRevisionsItemsCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [bimWorkOrderPriceRevisionsItemsCount, getRowParams?.current?.endRow]);

    return {
        bimWorkOrderPriceRevisionsDataSource: dataSource,
        bimWorkOrderPriceRevisionsItemsCount,
        bimWorkOrderPriceRevisionsFetchedItemsCounter,
    };
};
